<script lang="ts" setup>
import formatMoney from '@/lib/formatters/money'
import type { Product } from '@/lib/types/Product'
import type { PayloadAddToCart } from '@/services/cart'
import { computed, ref } from 'vue'

const props = defineProps<{
  item: Product
  isBusy: boolean
  dictionary: Record<string, string>
}>()

const emit = defineEmits<{
  (e: 'add', data: PayloadAddToCart): void
}>()

const url = computed(() => unescape(props.item.url))

const t = (label: string) => {
  return props.dictionary[label] || label
}

const hasVariants = computed(() => Object.keys(props.item.variants).length > 1)

const selectedVariant = computed(() => {
  if (props.item) {
    return props.item.variants[props.item.selectedVariantId]
  } else {
    return null
  }
})
const price = computed(() => {
  if (selectedVariant.value) {
    return parseInt(selectedVariant.value.price)
  } else {
    return 0
  }
})

const subscribePrice = computed(() => {
  if (selectedVariant.value) {
    return selectedVariant.value.sellingPlanAllocations &&
      selectedVariant.value.sellingPlanAllocations[0]
      ? selectedVariant.value.sellingPlanAllocations[0].price
      : null
  } else {
    return null
  }
})

const selectedVariantId = ref(props.item.selectedVariantId)
</script>

<template>
  <div class="CartItem CartItem--upsell">
    <div class="CartItem__Media">
      <img :src="item.image" :alt="item.title" width="100" />
    </div>
    <div class="CartItem__Body Stack Stack--tiny">
      <p class="CartItem__Title">
        <a :href="url">
          {{ item.title }}
        </a>
      </p>
      <div class="CartItem__Footer">
        <select
          v-if="hasVariants"
          v-model="selectedVariantId"
          class="CartItem__VariantSelector"
        >
          <option
            v-for="variant in item.variants"
            :key="variant.id"
            :value="variant.id"
          >
            {{ variant.title }}
          </option>
        </select>
        <span class="CartItem__Price">
          {{ formatMoney(subscribePrice !== null ? subscribePrice : price) }}
        </span>
        <button
          class="Button CartItem__AddButton"
          :class="{ 'Button--is-disabled': isBusy }"
          :disabled="isBusy"
          @click="
            emit('add', {
              id: item.id,
              selectedVariantId: selectedVariantId.toString(),
              attribution: { source: 'bower_upsell', view: 'bower_cart' },
              selectedSellingPlanId:
                selectedVariant?.sellingPlanAllocations[0]?.sellingPlan.id.toString(),
              quantity: 1,
            })
          "
        >
          {{ t('upsell_item_add_cta') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.CartItem.CartItem--upsell {
  border-top: none;
  margin: 0;
  padding: calc(var(--grid-gap)) 16px 0 16px;
}

.CartItem__VariantSelector {
  width: 50%;
}

.CartItem__AddButton {
  margin-left: 12px;
}
</style>
